import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

/**
 * Generate a professional PDF Statement of Account
 */
export function exportStatementToPDF({
  type = "Supplier", // "Supplier" or "Customer"
  party = {},
  storeSettings = {},
  transactions = [],
  summary = {},
  dateRange = "All Time",
  currency = "Rs."
}) {
  const doc = new jsPDF("p", "pt", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();

  const businessName = storeSettings?.store_name || storeSettings?.storeName || "RESTAURANT / HOTEL";
  const businessAddress = storeSettings?.address || "";
  const businessPhone = storeSettings?.phone || "";
  const businessEmail = storeSettings?.email || "";
  const businessPan = storeSettings?.pan_no || storeSettings?.panNo || "";

  const partyName = party?.name || party?.customer_name || "Party";
  const partyPhone = party?.phone || party?.customer_id || "";
  const partyEmail = party?.email || party?.customer_email || "";
  const partyPan = party?.pan_no || party?.customer_pan || "";
  const partyAddress = party?.address || party?.customer_address || "";

  // 1. Company Header
  doc.setFillColor(30, 41, 59); // Dark slate header banner
  doc.rect(0, 0, pageWidth, 85, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(255, 255, 255);
  doc.text(businessName.toUpperCase(), 40, 35);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(203, 213, 225);
  const headerSub = [
    businessAddress,
    businessPhone ? `Tel: ${businessPhone}` : "",
    businessPan ? `PAN/VAT: ${businessPan}` : "",
  ].filter(Boolean).join("  |  ");
  doc.text(headerSub, 40, 52);

  // Statement Badge
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(255, 255, 255);
  const titleText = `${type.toUpperCase()} STATEMENT OF ACCOUNT`;
  doc.text(titleText, pageWidth - 40, 45, { align: "right" });

  doc.setFontSize(8.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(148, 163, 184);
  doc.text(`Period: ${dateRange}`, pageWidth - 40, 60, { align: "right" });

  // 2. Party & Statement Details Box
  let y = 105;
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(40, y, pageWidth - 80, 75, 6, 6, "FD");

  // Left side: Party Info
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.text(`${type.toUpperCase()} DETAILS:`, 55, y + 18);

  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text(partyName, 55, y + 33);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  const pInfo = [
    partyPhone ? `Phone: ${partyPhone}` : "",
    partyPan ? `PAN/VAT: ${partyPan}` : "",
    partyEmail ? `Email: ${partyEmail}` : "",
    partyAddress ? `Address: ${partyAddress}` : ""
  ].filter(Boolean).join("  •  ");
  doc.text(pInfo || "No additional contact info", 55, y + 48);

  // Right side: Financial Summary Pills
  const netDue = Number(summary?.net_due || summary?.current_balance || 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text("CURRENT OUTSTANDING BALANCE", pageWidth - 55, y + 20, { align: "right" });

  doc.setFontSize(15);
  doc.setTextColor(netDue > 0 ? (type === "Supplier" ? 220 : 37) : 22, netDue > 0 ? (type === "Supplier" ? 38 : 99) : 163, netDue > 0 ? (type === "Supplier" ? 38 : 235) : 74);
  doc.text(`${currency} ${netDue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, pageWidth - 55, y + 42, { align: "right" });

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(148, 163, 184);
  doc.text(`Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, pageWidth - 55, y + 58, { align: "right" });

  // 3. Transactions Table
  const tableHead = [["Date", "Voucher / Type", "Ref #", "Description", "Debit", "Credit", "Balance"]];
  const tableBody = transactions.map((t) => [
    t.date ? new Date(t.date).toLocaleDateString() : "-",
    t.type || "-",
    t.ref_no || "-",
    t.description || "-",
    t.debit > 0 ? `${currency} ${Number(t.debit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-",
    t.credit > 0 ? `${currency} ${Number(t.credit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-",
    `${currency} ${Number(t.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  ]);

  autoTable(doc, {
    startY: y + 90,
    head: tableHead,
    body: tableBody,
    theme: "striped",
    headStyles: {
      fillColor: [30, 41, 59],
      textColor: [255, 255, 255],
      fontSize: 8.5,
      fontStyle: "bold",
      halign: "left"
    },
    styles: {
      fontSize: 8,
      cellPadding: 6,
      textColor: [30, 41, 59],
      lineColor: [241, 245, 249],
      lineWidth: 0.5
    },
    columnStyles: {
      0: { cellWidth: 65 },
      1: { cellWidth: 75 },
      2: { cellWidth: 70 },
      3: { cellWidth: "auto" },
      4: { cellWidth: 75, halign: "right" },
      5: { cellWidth: 75, halign: "right" },
      6: { cellWidth: 80, halign: "right", fontStyle: "bold" }
    },
    margin: { left: 40, right: 40 },
    didDrawPage: (data) => {
      // Footer page numbering
      const str = `Page ${doc.internal.getNumberOfPages()}`;
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(str, pageWidth / 2, doc.internal.pageSize.getHeight() - 20, { align: "center" });
    }
  });

  // 4. Summary & Totals
  let finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 20 : 500;
  if (finalY > 700) {
    doc.addPage();
    finalY = 50;
  }

  const totalDebit = Number(summary?.total_purchases || summary?.total_invoiced || 0);
  const totalCredit = Number(summary?.total_paid || summary?.total_received || 0);

  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(pageWidth - 260, finalY, 220, 75, 4, 4, "FD");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(`Total ${type === "Supplier" ? "Purchases" : "Invoiced"}:`, pageWidth - 250, finalY + 20);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(15, 23, 42);
  doc.text(`${currency} ${totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, pageWidth - 50, finalY + 20, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 116, 139);
  doc.text(`Total ${type === "Supplier" ? "Paid" : "Received"}:`, pageWidth - 250, finalY + 38);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(22, 163, 74);
  doc.text(`${currency} ${totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, pageWidth - 50, finalY + 38, { align: "right" });

  doc.setDrawColor(203, 213, 225);
  doc.line(pageWidth - 250, finalY + 46, pageWidth - 50, finalY + 46);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(15, 23, 42);
  doc.text("Net Balance Due:", pageWidth - 250, finalY + 62);
  doc.setTextColor(netDue > 0 ? (type === "Supplier" ? 220 : 37) : 22, netDue > 0 ? (type === "Supplier" ? 38 : 99) : 163, netDue > 0 ? (type === "Supplier" ? 38 : 235) : 74);
  doc.text(`${currency} ${netDue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, pageWidth - 50, finalY + 62, { align: "right" });

  // 5. Signature Blocks
  finalY += 120;
  if (finalY > 760) {
    doc.addPage();
    finalY = 80;
  }
  doc.setDrawColor(203, 213, 225);
  doc.line(40, finalY, 160, finalY);
  doc.line(pageWidth - 160, finalY, pageWidth - 40, finalY);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text("Prepared By", 40, finalY + 14);
  doc.text("Authorized Signature", pageWidth - 160, finalY + 14);

  // Save the PDF
  const filename = `${type}_Statement_${partyName.replace(/\s+/g, "_")}_${new Date().toISOString().substring(0, 10)}.pdf`;
  doc.save(filename);
}

/**
 * Export Statement to Excel (.xlsx)
 */
export function exportStatementToExcel({
  type = "Supplier",
  party = {},
  storeSettings = {},
  transactions = [],
  summary = {},
  dateRange = "All Time",
  currency = "Rs."
}) {
  const businessName = storeSettings?.store_name || storeSettings?.storeName || "Restaurant / Hotel";
  const partyName = party?.name || party?.customer_name || "Party";
  const partyPhone = party?.phone || party?.customer_id || "";

  const rows = [];
  rows.push([businessName.toUpperCase()]);
  rows.push([`${type.toUpperCase()} STATEMENT OF ACCOUNT`]);
  rows.push([`Period: ${dateRange}`]);
  rows.push([`${type}: ${partyName} | Phone: ${partyPhone} | Generated: ${new Date().toLocaleString()}`]);
  rows.push([]); // empty line

  rows.push(["Date", "Voucher Type", "Reference #", "Description", "Debit", "Credit", "Running Balance"]);

  transactions.forEach((t) => {
    rows.push([
      t.date ? new Date(t.date).toLocaleDateString() : "",
      t.type || "",
      t.ref_no || "",
      t.description || "",
      Number(t.debit || 0),
      Number(t.credit || 0),
      Number(t.balance || 0)
    ]);
  });

  rows.push([]);
  rows.push([
    "SUMMARY TOTALS",
    "",
    "",
    "",
    Number(summary?.total_purchases || summary?.total_invoiced || 0),
    Number(summary?.total_paid || summary?.total_received || 0),
    Number(summary?.net_due || summary?.current_balance || 0)
  ]);

  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Statement");

  const filename = `${type}_Statement_${partyName.replace(/\s+/g, "_")}_${new Date().toISOString().substring(0, 10)}.xlsx`;
  XLSX.writeFile(workbook, filename);
}

/**
 * Print Statement in browser window
 */
export function printStatementHTML({
  type = "Supplier",
  party = {},
  storeSettings = {},
  transactions = [],
  summary = {},
  dateRange = "All Time",
  currency = "Rs."
}) {
  const businessName = storeSettings?.store_name || storeSettings?.storeName || "Restaurant / Hotel";
  const businessAddress = storeSettings?.address || "";
  const businessPhone = storeSettings?.phone || "";
  const businessPan = storeSettings?.pan_no || storeSettings?.panNo || "";

  const partyName = party?.name || party?.customer_name || "Party";
  const partyPhone = party?.phone || party?.customer_id || "";
  const partyPan = party?.pan_no || party?.customer_pan || "";
  const partyAddress = party?.address || party?.customer_address || "";

  const totalDebit = Number(summary?.total_purchases || summary?.total_invoiced || 0);
  const totalCredit = Number(summary?.total_paid || summary?.total_received || 0);
  const netDue = Number(summary?.net_due || summary?.current_balance || 0);

  const printWindow = window.open("", "_blank");
  if (!printWindow) return;

  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${type} Statement - ${partyName}</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; margin: 20px; color: #1e293b; }
          .header { border-bottom: 2px solid #0f172a; padding-bottom: 12px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: flex-end; }
          .company-name { font-size: 24px; font-weight: 800; color: #0f172a; margin: 0; }
          .company-sub { font-size: 11px; color: #64748b; margin: 4px 0 0 0; }
          .title { font-size: 18px; font-weight: 800; color: #0f172a; text-align: right; margin: 0; }
          .period { font-size: 11px; color: #64748b; text-align: right; margin-top: 4px; }
          .party-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px 18px; margin-bottom: 20px; display: flex; justify-content: space-between; }
          .party-title { font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; margin-bottom: 4px; }
          .party-name { font-size: 16px; font-weight: 800; color: #0f172a; }
          .party-meta { font-size: 11px; color: #64748b; margin-top: 2px; }
          .due-box { text-align: right; }
          .due-amount { font-size: 22px; font-weight: 900; color: ${netDue > 0 ? (type === "Supplier" ? "#dc2626" : "#2563eb") : "#16a34a"}; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 11px; }
          th { background: #0f172a; color: #fff; padding: 8px 10px; text-align: left; font-weight: 700; }
          td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; }
          tr:nth-child(even) { background: #f8fafc; }
          .text-right { text-align: right; }
          .summary-card { width: 280px; margin-left: auto; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 16px; font-size: 12px; margin-bottom: 40px; }
          .summary-row { display: flex; justify-content: space-between; padding: 4px 0; }
          .summary-row.total { border-top: 1px solid #cbd5e1; font-weight: 800; padding-top: 8px; margin-top: 4px; font-size: 13px; }
          .signatures { display: flex; justify-content: space-between; margin-top: 60px; font-size: 11px; color: #64748b; }
          .sig-line { width: 180px; border-top: 1px solid #cbd5e1; padding-top: 6px; text-align: center; }
          @media print {
            body { margin: 0; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1 class="company-name">${businessName.toUpperCase()}</h1>
            <p class="company-sub">${[businessAddress, businessPhone ? `Phone: ${businessPhone}` : "", businessPan ? `PAN/VAT: ${businessPan}` : ""].filter(Boolean).join("  •  ")}</p>
          </div>
          <div>
            <h2 class="title">${type.toUpperCase()} STATEMENT</h2>
            <p class="period">Period: ${dateRange}</p>
          </div>
        </div>

        <div class="party-box">
          <div>
            <div class="party-title">${type} Information</div>
            <div class="party-name">${partyName}</div>
            <div class="party-meta">${[partyPhone ? `Tel: ${partyPhone}` : "", partyPan ? `PAN: ${partyPan}` : "", partyAddress].filter(Boolean).join("  •  ")}</div>
          </div>
          <div class="due-box">
            <div class="party-title">Outstanding Balance</div>
            <div class="due-amount">${currency} ${netDue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Voucher / Type</th>
              <th>Ref #</th>
              <th>Description</th>
              <th class="text-right">Debit</th>
              <th class="text-right">Credit</th>
              <th class="text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            ${transactions.map(t => `
              <tr>
                <td>${t.date ? new Date(t.date).toLocaleDateString() : "-"}</td>
                <td>${t.type || "-"}</td>
                <td>${t.ref_no || "-"}</td>
                <td>${t.description || "-"}</td>
                <td class="text-right">${t.debit > 0 ? `${currency} ${Number(t.debit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}</td>
                <td class="text-right">${t.credit > 0 ? `${currency} ${Number(t.credit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}</td>
                <td class="text-right" style="font-weight: 700;">${currency} ${Number(t.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              </tr>
            `).join("")}
          </tbody>
        </table>

        <div class="summary-card">
          <div class="summary-row">
            <span>Total ${type === "Supplier" ? "Purchases" : "Invoiced"}:</span>
            <span>${currency} ${totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div class="summary-row">
            <span>Total ${type === "Supplier" ? "Paid" : "Received"}:</span>
            <span style="color: #16a34a; font-weight: 700;">${currency} ${totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div class="summary-row total">
            <span>Net Balance Due:</span>
            <span style="color: ${netDue > 0 ? (type === "Supplier" ? "#dc2626" : "#2563eb") : "#16a34a"};">${currency} ${netDue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>

        <div class="signatures">
          <div class="sig-line">Prepared By</div>
          <div class="sig-line">Authorized Signatory</div>
        </div>

        <script>
          window.onload = function() {
            window.print();
          };
        </script>
      </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
}
